import { SerdeDtoClass } from '@zaoblako/shared/modules/serde/classes/serde-dto.class';
import { DocumentDtoKeysEnum } from '@zaoblako/shared/modules/document/enums/document-dto-keys.enum';
import { DtoMessageEnum } from '@zaoblako/shared/common/enums/dto.message.enum';
import { entityLikeSchema, entityLikeSchemaUuid } from '@zaoblako/shared/modules/serde/schemas/entity-like.schema';
import { ClientCheckerKeysEnum } from '@zaoblako/shared/modules/client/enums/client-checker-keys.enum';
import { CatalogCheckersEnum } from '@zaoblako/shared/modules/catalog/enums/catalog-checkers.enum';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { ContragentFindInterface } from '@zaoblako/shared/modules/client/dtos/contragent/contragent-find.dto';
import type { ContractFindInterface } from '@zaoblako/shared/modules/document/dtos/contract/contract-find.dto';
import type { OperationTypeFindInterface } from '@zaoblako/shared/modules/document/dtos/operation-type/operation-type-find.dto';
import type { FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import type { UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { DealFindInterface } from '@zaoblako/shared/modules/business/dto/deal/deal-find.dto';
import type { NdsFindInterface } from '@zaoblako/shared/modules/finance/dtos/nds-find.dto';
import type { BankDetailsFindInterface } from '@zaoblako/shared/modules/client/dtos/bank.details/bank-details-find.dto';
import type { CostCenterFindInterface } from '@zaoblako/shared/modules/catalog/dtos/cost-center/cost-center-find.dto';
import type { CurrencyFindInterface } from '@zaoblako/shared/modules/catalog/dtos/currency/currency-find.dto';

import * as yup from 'yup';

/** Document create/update APIs request body */
export interface DocumentFormInterface {
  // Data columns
  /** Document *operation* date and time */
  date: Date;
  /** Document amount */
  amount: number;
  /**
   * Document Value-Added Tax (VAT, rus. "НДС") amount value.
   * Can be computed by {@link DocumentFormDto.amount} value.
   */
  vatAmount: number;
  /** Additional commentary for document (only for manual created documents) */
  commentary?: string;
  /** Locked document does not accept updates */
  locked?: boolean;

  // Relations
  /** The company this document is associated with ("source" organization) */
  company?: CompanyFindInterface;
  /** The counterparty this document is associated with ("target" organization) */
  contragent?: ContragentFindInterface;
  /** Type of operation (only for documents exported from 1C ERP databases) */
  operationType?: OperationTypeFindInterface;
  /** The filial this document is associated with */
  filial?: FilialFindInterface;
  /** The unit this document is associated with */
  unit?: UnitFindInterface;
  /** The direction this document is associated with */
  direction?: DirectionFindInterface;
  /** The deal this document is associated with */
  deal?: DealFindInterface;
  /** Selected Value-Added Tax */
  nds?: NdsFindInterface;
  /** Selected bank details */
  bankDetails?: BankDetailsFindInterface;
  /** Selected contract */
  contract?: ContractFindInterface;
  /** Selected cost center */
  costCenter?: CostCenterFindInterface;
  /** Selected currency */
  currency?: CurrencyFindInterface;
}

/** Document create/update APIs request body - DTO schema */
export const DocumentFormDto = SerdeDtoClass.create<DocumentFormInterface>(DocumentDtoKeysEnum.DOCUMENT_FORM_DTO, {
  date: {
    label: 'Дата документа',
    serializer: yup.date(),
  },
  amount: {
    label: 'Сумма',
    hint: 'Сумма документа без знака',
    serializer: yup
      .number()
      .positive(DtoMessageEnum.NOT_POSITIVE),
  },
  vatAmount: {
    label: 'Сумма НДС',
    hint: 'Сумма НДС документа без знака',
    serializer: yup
      .number()
      .min(0, DtoMessageEnum.NOT_ZERO_OR_POSITIVE),
  },
  commentary: {
    optional: true,
    label: 'Комментарий',
    hint: 'Пользовательский комментарий для документа',
    serializer: yup.string(),
  },
  locked: {
    optional: true,
    label: 'Игнорировать новые изменения',
    hint: 'Если включено, то новые изменения в документе не будут приниматься',
    serializer: yup.boolean(),
  },
  company: {
    optional: true,
    label: 'Компания',
    serializer: entityLikeSchema,
    checkers: [ClientCheckerKeysEnum.COMPANY_ASSERT_EXISTS_BY_DTO],
  },
  contragent: {
    optional: true,
    label: 'Контрагент',
    serializer: entityLikeSchema,
    checkers: [ClientCheckerKeysEnum.CONTRAGENT_ASSERT_EXISTS_BY_DTO],
  },
  operationType: {
    optional: true,
    label: 'Вид операции',
    hint: 'Вид операции определяет знак для суммы и НДС документа',
    serializer: entityLikeSchema,
  },
  filial: {
    optional: true,
    label: 'Филиал',
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.FILIAL_ASSERT_EXISTS_BY_DTO],
  },
  unit: {
    optional: true,
    label: 'БЕ',
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.UNIT_ASSERT_EXISTS_BY_DTO],
  },
  direction: {
    optional: true,
    label: 'БЕ',
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.DIRECTION_ASSERT_EXISTS_BY_DTO],
  },
  deal: {
    optional: true,
    label: 'Сделка',
    serializer: entityLikeSchema,
  },
  nds: {
    optional: true,
    label: 'Ставка НДС',
    serializer: entityLikeSchema,
  },
  bankDetails: {
    optional: true,
    label: 'Рассчетный счет',
    serializer: entityLikeSchema,
  },
  contract: {
    optional: true,
    label: 'Договор',
    serializer: entityLikeSchema,
  },
  costCenter: {
    optional: true,
    label: 'Центр затрат',
    serializer: entityLikeSchema,
    checkers: [CatalogCheckersEnum.COST_CENTER_ASSERT_EXISTS_BY_DTO],
  },
  currency: {
    optional: true,
    label: 'Валюта',
    serializer: entityLikeSchemaUuid,
    checkers: [CatalogCheckersEnum.CURRENCY_ASSERT_EXISTS_BY_DTO],
  },
});
